import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react';
import { MapPin } from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Badge } from '@clay-run/glaze/badge';
import { Button, ButtonLink } from '@clay-run/glaze/button';
import { InputField, InputIcon } from '@clay-run/glaze/input-field';
import { ModalHeader, ModalBody, ModalActions } from '@clay-run/glaze/modal';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { AUTOFILL_IGNORE_PROPS } from '../autofill';
import type { Campaign } from '../launcher-campaign.types';
import type { PatchCampaignFields } from '../launcher-campaign-schema';
import { Section, StaticField } from './glaze-panel-helpers';

// ── Helpers ──────────────────────────────────────────────────────────────────
//
// Glaze supplies every form control and chrome piece here (InputField /
// TextareaField / SwitchField / Button / ButtonLink / Badge / Alert /
// ModalHeader / ModalBody / ModalActions). `Section` and `StaticField` — the
// two shapes Glaze has no component for — live in `./glaze-panel-helpers` so
// `CampaignMemberSidebar.tsx` (U10) can reuse them without duplicating.

// ── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  status: string;
  start_date: string;
  end_date: string;
  city: string;
  state: string;
  capacity: string;
  budgeted_cost: string;
  description: string;
  event_brief_url: string;
  target_persona: string;
  goal: string;
  registration_goal: string;
  pipeline_goal: string;
  arr_goal: string;
  partner: string;
  partner_activation_items: string;
  invite_subject: string;
  invite_body: string;
  playhouse_visible: boolean;
}

function campaignToForm(c: Campaign): FormState {
  return {
    status: c.status || '',
    start_date: c.start_date ?? c.event_date ?? '',
    end_date: c.end_date ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    capacity: c.capacity != null ? String(c.capacity) : '',
    budgeted_cost: c.budgeted_cost != null ? String(c.budgeted_cost) : '',
    description: c.description ?? '',
    event_brief_url: c.event_brief_url ?? '',
    target_persona: c.target_persona ?? '',
    goal: c.goal ?? '',
    registration_goal: c.registration_goal != null ? String(c.registration_goal) : '',
    pipeline_goal: c.pipeline_goal != null ? String(c.pipeline_goal) : '',
    arr_goal: c.arr_goal != null ? String(c.arr_goal) : '',
    partner: c.partner ?? '',
    partner_activation_items: c.partner_activation_items ?? '',
    invite_subject: c.invite_subject ?? '',
    invite_body: c.invite_body ?? '',
    playhouse_visible: c.playhouse_visible !== false,
  };
}

function formToPayload(form: FormState, original: FormState): PatchCampaignFields {
  const fields: PatchCampaignFields = {};
  if (form.status !== original.status) fields.status = form.status || undefined;
  if (form.start_date !== original.start_date) fields.start_date = form.start_date || null;
  if (form.end_date !== original.end_date) fields.end_date = form.end_date || null;
  if (form.city !== original.city) fields.city = form.city || null;
  if (form.state !== original.state) fields.state = form.state || null;
  if (form.capacity !== original.capacity) fields.capacity = form.capacity ? parseInt(form.capacity, 10) : null;
  if (form.budgeted_cost !== original.budgeted_cost) fields.budgeted_cost = form.budgeted_cost ? parseFloat(form.budgeted_cost) : null;
  if (form.description !== original.description) fields.description = form.description || null;
  if (form.event_brief_url !== original.event_brief_url) fields.event_brief_url = form.event_brief_url || null;
  if (form.target_persona !== original.target_persona) fields.target_persona = form.target_persona || null;
  if (form.goal !== original.goal) fields.goal = form.goal || null;
  if (form.registration_goal !== original.registration_goal) fields.registration_goal = form.registration_goal ? parseInt(form.registration_goal, 10) : null;
  if (form.pipeline_goal !== original.pipeline_goal) fields.pipeline_goal = form.pipeline_goal ? parseFloat(form.pipeline_goal) : null;
  if (form.arr_goal !== original.arr_goal) fields.arr_goal = form.arr_goal ? parseFloat(form.arr_goal) : null;
  if (form.partner !== original.partner) fields.partner = form.partner || null;
  if (form.partner_activation_items !== original.partner_activation_items) fields.partner_activation_items = form.partner_activation_items || null;
  if (form.invite_subject !== original.invite_subject) fields.invite_subject = form.invite_subject || null;
  if (form.invite_body !== original.invite_body) fields.invite_body = form.invite_body || null;
  if (form.playhouse_visible !== original.playhouse_visible) fields.playhouse_visible = form.playhouse_visible;
  return fields;
}

const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  start_date: 'Start Date',
  end_date: 'End Date',
  city: 'City',
  state: 'State',
  capacity: 'Capacity',
  budgeted_cost: 'Budgeted Cost',
  description: 'Description',
  event_brief_url: 'Event Brief URL',
  target_persona: 'Target Persona',
  goal: 'Goal',
  registration_goal: 'Registration Goal',
  pipeline_goal: 'Pipeline Goal',
  arr_goal: 'ARR Goal',
  partner: 'Partner',
  partner_activation_items: 'Activation Items',
  invite_subject: 'Subject',
  invite_body: 'Body',
  playhouse_visible: 'Show in Playhouse',
};

// ── Component ────────────────────────────────────────────────────────────────

interface CampaignDetailSidebarProps {
  campaign: Campaign;
  onClose: () => void;
  onSave?: (fields: PatchCampaignFields) => Promise<void>;
  saving?: boolean;
  /**
   * Renders every editable control as its `StaticField` read-only
   * counterpart, drops `ModalActions` down to a single "Close" button, and
   * retitles the header "Campaign Details". `onSave` is never called.
   * `readOnly` false (the default) is byte-for-byte today's editable
   * behaviour — escape handling, width, and tokens unchanged.
   */
  readOnly?: boolean;
}

export function CampaignDetailSidebar({ campaign, onClose, onSave, saving, readOnly = false }: CampaignDetailSidebarProps) {
  const original = useMemo(() => campaignToForm(campaign), [campaign]);
  const [form, setForm] = useState<FormState>(original);

  // Reset form when campaign changes
  useEffect(() => {
    setForm(campaignToForm(campaign));
  }, [campaign]);

  const set = useCallback(<K extends keyof FormState>(key: K, val: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: val }));
  }, []);

  const [error, setError] = useState<string | null>(null);

  const pendingPayload = useMemo(() => formToPayload(form, original), [form, original]);
  const pendingKeys = Object.keys(pendingPayload) as (keyof PatchCampaignFields)[];
  const hasPending = pendingKeys.length > 0;

  const handleSave = useCallback(async () => {
    if (!hasPending || !onSave) return;
    setError(null);
    try {
      await onSave(pendingPayload);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    }
  }, [hasPending, pendingPayload, onSave]);

  const sfdcUrl = `https://clay.lightning.force.com/lightning/r/Campaign/${campaign.id}/view`;

  // Product decision: only the explicit close controls (ModalHeader's close
  // button, the footer's Cancel button) dismiss this panel — never Escape.
  // `disablePointerDismissal` (below) already blocks outside-click, but
  // base-ui's `useDialogRoot` wires `escapeKey: isTopmost` into its dismiss
  // handling unconditionally (confirmed against
  // node_modules/@base-ui/react/dialog/root/useDialogRoot.js and
  // DialogRoot.d.ts — there is no prop to disable it), so an accidental
  // Escape press while editing would otherwise silently discard unsaved
  // field changes (every caller unmounts on `onClose` with no
  // unsaved-changes guard). Filter it out here via the `reason` on
  // `onOpenChange`'s second argument and cancel base-ui's own handling of it.
  const handleOpenChange = useCallback(
    (_open: boolean, eventDetails: DialogPrimitive.Root.ChangeEventDetails) => {
      if (eventDetails.reason === 'escape-key') {
        eventDetails.cancel();
        return;
      }
      onClose();
    },
    [onClose],
  );

  return (
    <DialogPrimitive.Root open onOpenChange={handleOpenChange} modal={false} disablePointerDismissal>
      {/* z-index lives on the Portal, never the Popup — a nested Glaze popup
          (Combobox/Select) opened inside this drawer is portaled INTO this
          same node by Base UI, so a z-index on the Popup would paint over it.
          Twin fix: the playbooks mini-app's PushDrawer.tsx (cross-app imports
          are lint-forbidden, so the literals are duplicated here). */}
      <DialogPrimitive.Portal className="relative z-50">
        <DialogPrimitive.Popup className="fixed inset-y-0 right-0 flex w-[480px] flex-col border-l border-border-secondary bg-bg-primary shadow-lg">
          <ModalHeader onClose={onClose}>{readOnly ? 'Campaign Details' : 'Edit Campaign Details'}</ModalHeader>

          {/* Body */}
          <ModalBody className="flex-1 space-y-6 overflow-y-auto px-5 pt-4 pb-5" padded={false}>
            {/* Read-only fields */}
            <Section title="Campaign">
              <ButtonLink variant="secondary" href={sfdcUrl} target="_blank" rel="noopener noreferrer">
                View in Salesforce
              </ButtonLink>
              <StaticField label="Name" value={campaign.name} />
              <StaticField
                label="Type / Subtype"
                value={`${campaign.type}${campaign.subtype ? ` · ${campaign.subtype}` : ''}`}
              />
            </Section>

            <Section title="Playhouse">
              {readOnly ? (
                <StaticField label="Show in Playhouse" value={form.playhouse_visible ? 'Yes' : 'No'} />
              ) : (
                <SwitchField
                  label="Show in Playhouse"
                  checked={form.playhouse_visible}
                  onCheckedChange={checked => set('playhouse_visible', checked)}
                />
              )}
            </Section>

            <Section title="Event Details">
              {readOnly ? (
                <StaticField label="Status" value={form.status} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Status"
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                />
              )}
              <div className="grid grid-cols-2 gap-4">
                {readOnly ? (
                  <StaticField label="Start Date" value={form.start_date} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="Start Date"
                    type="date"
                    value={form.start_date}
                    onChange={e => set('start_date', e.target.value)}
                  />
                )}
                {readOnly ? (
                  <StaticField label="End Date" value={form.end_date} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="End Date"
                    type="date"
                    value={form.end_date}
                    onChange={e => set('end_date', e.target.value)}
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {readOnly ? (
                  <StaticField label="City" value={form.city} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="City"
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    trailingElement={<InputIcon type={MapPin} />}
                  />
                )}
                {readOnly ? (
                  <StaticField label="State" value={form.state} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="State"
                    value={form.state}
                    onChange={e => set('state', e.target.value)}
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {readOnly ? (
                  <StaticField label="Capacity" value={form.capacity} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="Capacity"
                    type="number"
                    value={form.capacity}
                    onChange={e => set('capacity', e.target.value)}
                  />
                )}
                {readOnly ? (
                  <StaticField label="Budgeted Cost" value={form.budgeted_cost} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="Budgeted Cost"
                    type="number"
                    placeholder="$"
                    value={form.budgeted_cost}
                    onChange={e => set('budgeted_cost', e.target.value)}
                  />
                )}
              </div>
              {readOnly ? (
                <StaticField label="Description" value={form.description} />
              ) : (
                <TextareaField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Description"
                  rows={4}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                />
              )}
              {readOnly ? (
                <StaticField label="Event Brief URL" value={form.event_brief_url} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Event Brief URL"
                  type="url"
                  placeholder="https://"
                  value={form.event_brief_url}
                  onChange={e => set('event_brief_url', e.target.value)}
                />
              )}
              {readOnly ? (
                <StaticField label="Target Persona" value={form.target_persona} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Target Persona"
                  value={form.target_persona}
                  onChange={e => set('target_persona', e.target.value)}
                />
              )}
            </Section>

            <Section title="Goals">
              {readOnly ? (
                <StaticField label="Goal" value={form.goal} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Goal"
                  value={form.goal}
                  onChange={e => set('goal', e.target.value)}
                />
              )}
              <div className="grid grid-cols-3 gap-4">
                {readOnly ? (
                  <StaticField label="Registration" value={form.registration_goal} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="Registration"
                    type="number"
                    value={form.registration_goal}
                    onChange={e => set('registration_goal', e.target.value)}
                  />
                )}
                {readOnly ? (
                  <StaticField label="Pipeline" value={form.pipeline_goal} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="Pipeline"
                    type="number"
                    value={form.pipeline_goal}
                    onChange={e => set('pipeline_goal', e.target.value)}
                  />
                )}
                {readOnly ? (
                  <StaticField label="ARR" value={form.arr_goal} />
                ) : (
                  <InputField
                    {...AUTOFILL_IGNORE_PROPS}
                    label="ARR"
                    type="number"
                    value={form.arr_goal}
                    onChange={e => set('arr_goal', e.target.value)}
                  />
                )}
              </div>
            </Section>

            <Section title="Partner">
              {readOnly ? (
                <StaticField label="Partner" value={form.partner} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Partner"
                  value={form.partner}
                  onChange={e => set('partner', e.target.value)}
                />
              )}
              {readOnly ? (
                <StaticField label="Activation Items" value={form.partner_activation_items} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Activation Items"
                  value={form.partner_activation_items}
                  onChange={e => set('partner_activation_items', e.target.value)}
                />
              )}
            </Section>

            <Section title="Invite Copy">
              {readOnly ? (
                <StaticField label="Subject" value={form.invite_subject} />
              ) : (
                <InputField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Subject"
                  value={form.invite_subject}
                  onChange={e => set('invite_subject', e.target.value)}
                />
              )}
              {readOnly ? (
                <StaticField label="Body" value={form.invite_body} />
              ) : (
                <TextareaField
                  {...AUTOFILL_IGNORE_PROPS}
                  label="Body"
                  rows={6}
                  value={form.invite_body}
                  onChange={e => set('invite_body', e.target.value)}
                />
              )}
            </Section>
          </ModalBody>

          <ModalActions>
            {readOnly ? (
              <div className="flex w-full items-center justify-end">
                <Button variant="secondary" size="lg" onClick={onClose}>
                  Close
                </Button>
              </div>
            ) : (
              <div className="flex w-full flex-col gap-2">
                {error && <Alert status="error">{error}</Alert>}
                <div className="flex w-full items-center justify-between gap-3">
                  {hasPending ? (
                    <div className="flex min-w-0 flex-wrap items-center gap-1">
                      {pendingKeys.map(k => (
                        <Badge key={k} size="xs" color="yellow">
                          {FIELD_LABELS[k] ?? k}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-content-tertiary">No changes</p>
                  )}
                  <div className="flex shrink-0 gap-2">
                    <Button variant="secondary" size="lg" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      disabled={!hasPending || saving}
                      onClick={() => { void handleSave(); }}
                    >
                      {saving ? 'Saving to SFDC...' : 'Save to SFDC'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ModalActions>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
